import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { ArrowLeft, BookMarked, Loader, BookOpen, Tag, Network, Leaf } from 'lucide-react';
import ConceptQuizServices from './ConceptQuizServices';
import mathJaxConfig from './mathJaxConfig'; // Assurez-vous que le chemin est correct

// Configuration du nombre de questions pour un quiz de concept
const CONCEPT_QUIZ_COUNT = 5;

const ConceptQuiz = ({ 
  concept, 
  userId, 
  competenceId, // ID de la compétence parente pour mettre à jour sa progression
  onBack, 
  onComplete,
  onError
}) => {
  // Référence pour vérifier si le composant est monté
  const isMounted = useRef(true);
  
  // États du quiz
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
  const [totalQuestionsAnswered, setTotalQuestionsAnswered] = useState(0);
  const [error, setError] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [conceptData, setConceptData] = useState(null);
  
  // État pour gérer les onglets
  const [activeTab, setActiveTab] = useState('details'); // 'details' ou 'quiz'
  
  // Configuration de MathJax avec valeurs par défaut
  const mathJaxConfigWithFallback = {
    loader: { load: ['input/asciimath', 'output/chtml', 'ui/menu'] },
    asciimath: { delimiters: [['$', '$']] },
    ...mathJaxConfig
  };

  // Fonction pour obtenir l'ID du concept selon la structure backend
  // Format: nom_du_concept + "_" "D"+"_" + identifiant_du_document
  const getConceptId = (conceptObj) => {
    if (!conceptObj || !conceptObj.concept) {
      console.error("Objet concept invalide ou sans titre");
      return null;
    }
    
    // Vérifier si l'ID est déjà au format correct (s'il contient "_D_")
    if (conceptObj.id && conceptObj.id.includes("_D_")) {
      return conceptObj.id;
    }
    
    // Vérifier si nous avons un document associé pour extraire son ID
    if (!conceptObj.documents || !conceptObj.documents.length || !conceptObj.documents[0].id) {
      console.error("Pas de document associé au concept ou document sans ID");
      return null;
    }
    
    // Construire l'ID selon le format spécifié
    const documentId = conceptObj.documents[0].id;
    const conceptName = conceptObj.concept.replace(/\s+/g, '_');
    return `${conceptName}_D_${documentId}`;
  };

  // Initialisation - Charger les données du concept et l'historique du quiz
  useEffect(() => {
    let isActive = true; // Flag local pour le montage/démontage
    
    const initializeQuiz = async () => {
      if (!userId || !concept || !competenceId) {
        const errorMsg = "Données utilisateur, concept ou compétence manquantes";
        setError(errorMsg);
        if (onError) onError(errorMsg);
        setLoading(false);
        return;
      }
      
      // Utiliser directement l'objet concept complet
      setConceptData(concept);
      
      try {
        if (!concept.concept) {
          throw new Error("Le concept n'a pas de titre (champ 'concept')");
        }
        
        // Obtenir l'ID du concept selon la structure backend
        const conceptId = getConceptId(concept);
        if (!conceptId) {
          throw new Error("Impossible de générer l'ID du concept");
        }
        
        console.log("ID du concept pour le quiz:", conceptId);
        
        // Vérifier s'il existe déjà un quiz pour ce concept
        const existingQuiz = await ConceptQuizServices.getExistingQuiz(userId, competenceId, conceptId);
        console.log("Quiz existant:", existingQuiz);
        
        if (!isActive) return;

        if (existingQuiz && existingQuiz.questions && existingQuiz.questions.length > 0) {
          console.log("Quiz existant trouvé, chargement des données");
          // Quiz existant trouvé
          setQuizQuestions(existingQuiz.questions);
          setCurrentQuestionIndex(existingQuiz.currentQuestionIndex || 0);
          setQuizCompleted(existingQuiz.completed || false);
          
          // Charger la progression
          if (existingQuiz.progress) {
            setTotalCorrectAnswers(existingQuiz.progress.totalCorrectAnswers || 0);
            setTotalQuestionsAnswered(existingQuiz.progress.totalQuestionsAnswered || 0);
          }
        } else {
          console.log("Pas de quiz trouvé, génération d'un nouveau quiz");
          // Pas de quiz trouvé, en générer un nouveau
          await generateQuiz(concept, conceptId);
        }
      } catch (err) {
        console.error("Erreur d'initialisation du quiz:", err);
        if (isActive) {
          const errorMsg = "Erreur lors de l'initialisation du quiz";
          setError(errorMsg);
          if (onError) onError(err);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };
    
    initializeQuiz();
    
    return () => {
      isActive = false;
    };
  }, [concept, userId, competenceId, onError]);
  
  // Fonction pour générer un nouveau quiz
  const generateQuiz = async (conceptObj, conceptId) => {
    if (!conceptObj?.concept || !userId || !competenceId || !conceptId) {
      const errorMsg = "Titre du concept, utilisateur, compétence ou ID du concept manquant";
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }
    
    setGenerating(true);
    setError(null);
    
    try {
      // Vérifier si le composant est toujours monté
      if (!isMounted.current) return;
      
      // Préparer les données du concept pour la génération du quiz
      // Utiliser uniquement les champs concept (titre) et description
      const conceptForQuiz = {
        concept: conceptObj.concept,
        description: conceptObj.description
      };
      
      console.log("Génération du quiz pour le concept:", conceptForQuiz);
      
      // Utiliser le service pour générer les questions du quiz
      const parsedQuiz = await ConceptQuizServices.generateQuizQuestions(conceptForQuiz, CONCEPT_QUIZ_COUNT);
      
      // Vérifier si le composant est toujours monté
      if (!isMounted.current) return;
      
      // Mettre à jour l'état
      setQuizQuestions(parsedQuiz);
      setCurrentQuestionIndex(0);
      setAnswered(false);
      setSelectedAnswer('');
      setQuizCompleted(false);
      setTotalCorrectAnswers(0);
      setTotalQuestionsAnswered(0);
      
      // Sauvegarder dans Firestore avec l'ID du concept
      if (isMounted.current) {
        console.log("Sauvegarde du quiz avec l'ID:", conceptId);
        await ConceptQuizServices.saveQuiz(userId, competenceId, conceptId, parsedQuiz, 0, false);
      }
    } catch (err) {
      console.error("Erreur lors de la génération du quiz:", err);
      if (isMounted.current) {
        const errorMsg = "Erreur lors de la génération du quiz";
        setError(errorMsg);
        if (onError) onError(err);
      }
    } finally {
      if (isMounted.current) {
        setGenerating(false);
      }
    }
  };
  
  // Fonction pour le rendu du contenu avec MathJax
  const renderWithMathJax = useCallback((content) => {
    if (!content) return null;
    
    try {
      const preparedContent = content
        .replace(/\$\$/g, ' $$ ')
        .replace(/([^$])\$/g, '$1 $')
        .replace(/\$([^$])/g, '$ $1')
        .trim();
      
      return (
        <span className="math-wrapper">
          <MathJax inline dynamic>
            {preparedContent}
          </MathJax>
        </span>
      );
    } catch (error) {
      console.error("Erreur lors du rendu MathJax:", error);
      return <span>{content}</span>;
    }
  }, []);
  
  // Fonction pour sélectionner une option
  const handleOptionSelect = (option) => {
    if (answered) return; // Empêcher de changer de réponse après avoir soumis
    setSelectedAnswer(option);
  };
  
  // Fonction pour soumettre une réponse
  const handleSubmitAnswer = async () => {
    if (!quizQuestions || quizQuestions.length === 0 || !selectedAnswer || !conceptData) return;
    
    const currentQuestion = quizQuestions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.answer;
    
    // Afficher le feedback
    setFeedback(isCorrect ? currentQuestion.feedbackCorrect : currentQuestion.feedbackIncorrect);
    setAnswered(true);
    
    try {
      // Obtenir l'ID du concept selon la structure backend
      const conceptId = getConceptId(conceptData);
      if (!conceptId) {
        console.error("Impossible de générer l'ID du concept pour mettre à jour la progression");
        return;
      }
      
      // Vérifier si c'est la dernière question
      const isLastQuestion = currentQuestionIndex + 1 >= quizQuestions.length;
      const willComplete = isLastQuestion;
      
      // Mettre à jour la progression du concept ET de la compétence
      await ConceptQuizServices.updateProgress(
        userId, 
        competenceId, 
        conceptId, 
        isCorrect, 
        willComplete
      );
      
      // Vérifier si le composant est toujours monté
      if (!isMounted.current) return;
      
      // Mettre à jour les totaux locaux
      setTotalCorrectAnswers(prev => isCorrect ? prev + 1 : prev);
      setTotalQuestionsAnswered(prev => prev + 1);
      
      if (isLastQuestion) {
        setQuizCompleted(true);
        await ConceptQuizServices.saveQuiz(
          userId, 
          competenceId, 
          conceptId, 
          quizQuestions, 
          currentQuestionIndex, 
          true
        );
        if (onComplete) onComplete();
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour de la progression:", err);
      // Continuer même en cas d'erreur
    }
  };
  
  // Fonction pour passer à la question suivante
  const handleNextQuestion = async () => {
    if (!conceptData || !userId || !competenceId) return;
    
    setAnswered(false);
    setSelectedAnswer('');
    setFeedback('');
    
    // Obtenir l'ID du concept selon la structure backend
    const conceptId = getConceptId(conceptData);
    if (!conceptId) {
      console.error("Impossible de générer l'ID du concept pour passer à la question suivante");
      return;
    }
    
    if (currentQuestionIndex + 1 < quizQuestions.length) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      await ConceptQuizServices.saveQuiz(userId, competenceId, conceptId, quizQuestions, newIndex, false);
    } else {
      setQuizCompleted(true);
      await ConceptQuizServices.saveQuiz(userId, competenceId, conceptId, quizQuestions, currentQuestionIndex, true);
      if (onComplete) onComplete();
    }
  };
  
  // Fonction pour recommencer le quiz
  const handleRestartQuiz = async () => {
    if (!conceptData || !userId || !competenceId) return;
    
    // Obtenir l'ID du concept selon la structure backend
    const conceptId = getConceptId(conceptData);
    if (!conceptId) {
      console.error("Impossible de générer l'ID du concept pour redémarrer le quiz");
      return;
    }
    
    setLoading(true);
    await generateQuiz(conceptData, conceptId);
    setLoading(false);
  };
  
  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <div className="animate-spin">
          <Loader className="h-8 w-8 text-blue-500" />
        </div>
      </div>
    );
  }
  
  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="p-6 text-center">
        <h3 className="text-lg font-medium text-red-600">{error}</h3>
        <div className="mt-4 space-x-4">
          <button 
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
          {onBack && (
            <button 
              className="px-4 py-2 border border-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={onBack}
            >
              Retour
            </button>
          )}
        </div>
      </div>
    );
  }
  
  const currentQuestion = quizQuestions[currentQuestionIndex];
  const description = conceptData?.description || {};
  
  // Envelopper tout le contenu dans un try/catch pour éviter les crashs liés à MathJax
  try {
    return (
      <MathJaxContext config={mathJaxConfigWithFallback}>
        <div className="p-4">
          {/* En-tête avec titre et bouton de retour */}
          <div className="flex items-center mb-6">
            {onBack && (
              <button 
                onClick={onBack}
                className="mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Retour"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold">
                {conceptData?.concept || "Concept"}
              </h2>
              {conceptData?.category && (
                <span className="text-sm text-gray-500">
                  {conceptData.category}
                </span>
              )}
            </div>
          </div>
          
          {/* Système d'onglets */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-4 font-medium text-sm border-b-2 mr-2 ${
                  activeTab === 'details' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Détails du concept
              </button>
              <button
                onClick={() => setActiveTab('quiz')}
                className={`py-2 px-4 font-medium text-sm border-b-2 ${
                  activeTab === 'quiz' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quiz
              </button>
            </nav>
          </div>
          
          {/* Onglet détails du concept */}
          {activeTab === 'details' && (
            <div className="concept-details space-y-4">
              {/* Définition générale du concept */}
              {description && (
                <div className="space-y-4">
                  {/* Définition générale du concept */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                      <span>Définition</span>
                    </h3>
                    <p className="text-gray-700">
                      {description.description || description.résumé || description.definition || "Aucune définition disponible"}
                    </p>
                  </div>
                  
                  {/* Contexte d'utilisation */}
                  {description.resume && (
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                      <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                        <Tag className="h-4 w-4 text-emerald-600" />
                        <span>Résumé</span>
                      </h3>
                      <p className="text-gray-700">{description.resume}</p>
                    </div>
                  )}
                  
                  {/* Autres aspects du concept */}
                  {(description.importance || description.examples) && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      {description.importance && (
                        <div className="mb-3">
                          <h3 className="font-medium text-gray-800 mb-1">Importance</h3>
                          <p className="text-gray-700">{description.importance}</p>
                        </div>
                      )}
                      
                      {description.examples && (
                        <div className="mb-3">
                          <h3 className="font-medium text-gray-800 mb-1">Exemples</h3>
                          <p className="text-gray-700">{description.examples}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Concepts liés */}
                  {description.relatedConcepts && description.relatedConcepts.length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                      <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                        <Network className="h-4 w-4 text-purple-600" />
                        <span>Concepts liés</span>
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {description.relatedConcepts.map((relatedConcept, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-white text-purple-600 border border-purple-200 rounded-md text-sm flex items-center gap-1"
                          >
                            <Leaf className="h-3 w-3" />
                            {relatedConcept}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Bouton pour commencer le quiz */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setActiveTab('quiz')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors"
                >
                  Commencer le quiz
                </button>
              </div>
            </div>
          )}
          
          {/* Onglet quiz */}
          {activeTab === 'quiz' && (
            <div className="concept-quiz">
              {/* Progression */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">
                    Question {currentQuestionIndex + 1} sur {quizQuestions.length}
                  </span>
                  <span className="text-sm">
                    Score: {totalCorrectAnswers}/{totalQuestionsAnswered}
                    {totalQuestionsAnswered > 0 && ` (${Math.round((totalCorrectAnswers/totalQuestionsAnswered)*100)}%)`}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 rounded-full h-2" 
                    style={{ width: `${(currentQuestionIndex / Math.max(1, quizQuestions.length)) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Génération en cours */}
              {generating && (
                <div className="flex flex-col items-center p-12">
                  <div className="animate-spin mb-4">
                    <Loader className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="text-gray-700">
                    Génération du quiz en cours...
                  </p>
                </div>
              )}
              
              {/* Quiz complété */}
              {!generating && quizCompleted && (
                <div className="text-center p-6">
                  <h3 className="text-xl font-semibold mb-2">
                    Quiz terminé !
                  </h3>
                  <p className="text-lg mb-6">
                    Score final: {totalCorrectAnswers}/{Math.max(quizQuestions.length, totalQuestionsAnswered)}
                    {totalQuestionsAnswered > 0 && ` (${Math.round((totalCorrectAnswers/totalQuestionsAnswered)*100)}%)`}
                  </p>
                  
                  <div className="flex justify-center space-x-4">
                    <button 
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
                      onClick={handleRestartQuiz}
                    >
                      <BookMarked className="h-4 w-4 mr-2" />
                      Nouveau quiz
                    </button>
                    <button 
                      className="px-4 py-2 border border-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
                      onClick={() => setActiveTab('details')}
                    >
                      Voir détails du concept
                    </button>
                  </div>
                </div>
              )}
              
              {/* Question en cours */}
              {!generating && !quizCompleted && currentQuestion && (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-2">
                      {renderWithMathJax(currentQuestion.question)}
                    </h3>
                  </div>
                  
                  <ul className="space-y-2 mb-8">
                    {currentQuestion.options.map((option, index) => (
                      <li key={index}>
                        <button
                          onClick={() => handleOptionSelect(option)}
                          className={`w-full p-4 rounded-lg border text-left transition-colors ${
                            answered
                              ? option === currentQuestion.answer
                                ? 'bg-green-100 border-green-300' // Bonne réponse
                                : option === selectedAnswer
                                ? 'bg-red-100 border-red-300' // Mauvaise réponse
                                : 'bg-white border-gray-300'
                              : selectedAnswer === option
                              ? 'bg-blue-50 border-blue-300' // Option sélectionnée
                              : 'bg-white border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <span>
                            {renderWithMathJax(option)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  
                  {answered && feedback && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="font-semibold mb-2">
                        Feedback:
                      </h4>
                      <div>
                        {renderWithMathJax(feedback)}
                      </div>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-200 pt-4 mt-4 flex justify-between">
                    <button 
                      className="px-4 py-2 border border-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
                      onClick={() => setActiveTab('details')}
                    >
                      Voir détails
                    </button>
                    
                    <div>
                      {!answered ? (
                        <button
                          className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors ${
                            !selectedAnswer ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                          }`}
                          disabled={!selectedAnswer}
                          onClick={handleSubmitAnswer}
                        >
                          Valider
                        </button>
                      ) : (
                        <button
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          onClick={handleNextQuestion}
                        >
                          {currentQuestionIndex + 1 < quizQuestions.length ? 'Question suivante' : 'Terminer'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </MathJaxContext>
    );
  } catch (error) {
    console.error("Erreur lors du rendu du quiz:", error);
    return (
      <div className="p-6 text-center">
        <h3 className="text-lg font-medium text-red-600">
          Erreur d'affichage du quiz
        </h3>
        <p className="mb-4">Une erreur s'est produite lors de l'affichage du quiz.</p>
        {onBack && (
          <button 
            className="px-4 py-2 border border-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={onBack}
          >
            Retour
          </button>
        )}
      </div>
    );
  }
};

export default ConceptQuiz;